// src/components/formBuilder/Inspector.jsx
import React from "react";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Tooltip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Stack,
  IconButton,
  Select,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SelectInputComponent from "../ui/SelectInputComponent";
import ButtonComponent from "../ui/ButtonComponent";

// ---------- Validation Presets ----------
const PRESETS = [
  { value: "none", label: "No validation" },
  { value: "email", label: "Email address" },
  { value: "phone", label: "Phone number" },
  { value: "url", label: "Website URL" },
  { value: "zip", label: "ZIP / Postal code" },
  { value: "custom", label: "Custom (advanced)" },
];

const presetToPattern = (preset) => {
  switch (preset) {
    case "email":
      return {
        pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
        message: "Enter a valid email (e.g., name@example.com).",
      };
    case "phone":
      return { pattern: "^\\+?[0-9 .()-]{7,}$", message: "Enter a valid phone number." };
    case "url":
      return {
        pattern: "^(https?:\\/\\/)?[\\w.-]+\\.[a-zA-Z]{2,}.*$",
        message: "Enter a valid URL (e.g., https://example.com).",
      };
    case "zip":
      return { pattern: "^[A-Za-z0-9 -]{3,10}$", message: "Enter a valid postal code." };
    default:
      return { pattern: "", message: "" };
  }
};

export default function Inspector({
  schema,
  selectedField,
  onUpdateField,
  onUpdateSchemaMeta,
  readOnly = false,
}) {
  const [presetId, setPresetId] = React.useState("");
  const [mergeMode, setMergeMode] = React.useState("append");

  const disable = readOnly;

  // --- Notes Section Helpers (preserved) ---
  const handleAddNoteSection = () => {
    if (disable) return;
    const currentSections = schema.noteSections || [];
    onUpdateSchemaMeta({
      noteSections: [...currentSections, { title: "New Section", fieldNames: [] }],
    });
  };

  const handleRemoveNoteSection = (index) => {
    if (disable) return;
    const newSections = (schema.noteSections || []).filter((_, i) => i !== index);
    onUpdateSchemaMeta({ noteSections: newSections });
  };

  const handleUpdateNoteSection = (index, patch) => {
    if (disable) return;
    const newSections = [...(schema.noteSections || [])];
    newSections[index] = { ...newSections[index], ...patch };
    onUpdateSchemaMeta({ noteSections: newSections });
  };

  const fieldOptionsForSelect = (schema.fields || [])
    .filter((f) => f.type !== "section" && f.type !== "divider" && f.type !== "row" && f.type !== "column")
    .map((f) => ({ label: f.label || f.name, value: f.name }));

  const rowContainers = (schema.fields || [])
    .filter((f) => f.type === "row")
    .map((r) => ({ label: r.title || "Row", value: r.id }));

  // ---------- Form Settings ----------
  if (!selectedField) {
    return (
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Form Settings
        </Typography>

        <TextField
          label="Form Title"
          fullWidth
          sx={{ mb: 2 }}
          value={schema.title || ""}
          onChange={(e) => onUpdateSchemaMeta({ title: e.target.value })}
          helperText="Shown at the top of the form."
          disabled={disable}
        />

        <TextField
          label="Form Description"
          fullWidth
          multiline
          minRows={2}
          value={schema.description || ""}
          onChange={(e) => onUpdateSchemaMeta({ description: e.target.value })}
          helperText="A short summary to guide users."
          disabled={disable}
        />

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Notes Sections
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Define groups of fields to be formatted when copying notes from the Form Filler.
        </Typography>

        {(schema.noteSections || []).map((section, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Section {index + 1}</Typography>
              <Tooltip title="Remove Section">
                <span>
                  <IconButton
                    onClick={() => handleRemoveNoteSection(index)}
                    size="small"
                    disabled={disable}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            <TextField
              label="Section Title in Notes"
              fullWidth
              sx={{ mb: 2 }}
              value={section.title || ""}
              onChange={(e) => handleUpdateNoteSection(index, { title: e.target.value })}
              disabled={disable}
            />

            <SelectInputComponent
              label="Fields to Include"
              multiple
              fullWidth
              value={section.fieldNames || []}
              onChange={(e) => handleUpdateNoteSection(index, { fieldNames: e.target.value })}
              options={fieldOptionsForSelect}
              disabled={disable}
            />
          </Paper>
        ))}

        <ButtonComponent
          label="Add Note Section"
          type="secondary"
          onClick={handleAddNoteSection}
          startIcon={<AddCircleOutlineIcon />}
          fullWidth
          disabled={disable}
        />
      </Box>
    );
  }

  // ---------- Field-specific UI ----------
  const f = selectedField;
  const isSection = f.type === "section";
  const isDivider = f.type === "divider";
  const isRow = f.type === "row";
  const isColumn = f.type === "column";

  const set = (patch) => {
    if (disable) return;
    onUpdateField(f.id, patch);
  };

  const handleOptionChange = (index, prop, value) => {
    const newOptions = [...(f.options || [])];
    newOptions[index] = { ...newOptions[index], [prop]: value };
    if (prop === "label" && !newOptions[index].value) {
      newOptions[index].value = value;
    }
    set({ options: newOptions });
  };

  const handleAddOption = () => {
    const newOptions = [...(f.options || []), { label: "", value: "" }];
    set({ options: newOptions });
  };

  const handleRemoveOption = (index) => {
    const newOptions = (f.options || []).filter((_, i) => i !== index);
    set({ options: newOptions });
  };

  const handlePresetChange = (preset) => {
    if (!preset) return;
    if (preset === "custom") {
      set({ validationPreset: "custom" });
    } else {
      const { pattern, message } = presetToPattern(preset);
      set({ validationPreset: preset, pattern, patternMessage: message });
    }
  };

  const applyOptionPreset = () => {
    const preset = null;
    if (!preset) return;
  };

  // Row grouping controls for normal fields
  const showGroupingControls =
    !isSection && !isDivider && !isRow && !isColumn && Array.isArray(rowContainers) && rowContainers.length > 0;

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {isSection ? "Section" : isDivider ? "Divider" : isRow ? "Row Layout" : isColumn ? "Column Layout" : "Field"} Settings
      </Typography>

      {disable && (
        <Paper variant="outlined" sx={{ p: 1.25, mb: 2, bgcolor: "#FFF7ED", borderColor: "#FDBA74" }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#9A3412" }}>
            Read-only
          </Typography>
          <Typography variant="caption" sx={{ color: "#9A3412" }}>
            This template is published. Use "Clone to Edit" to create a draft copy.
          </Typography>
        </Paper>
      )}

      {isSection ? (
        <>
          <TextField
            label="Section Title"
            fullWidth
            sx={{ mb: 2 }}
            value={f.title || ""}
            onChange={(e) => set({ title: e.target.value })}
            disabled={disable}
          />
          <TextField
            label="Section Subtitle"
            fullWidth
            multiline
            minRows={2}
            value={f.subtitle || ""}
            onChange={(e) => set({ subtitle: e.target.value })}
            disabled={disable}
          />
        </>
      ) : isDivider ? (
        <TextField
          label="Divider Label"
          fullWidth
          sx={{ mb: 2 }}
          value={f.label || ""}
          onChange={(e) => set({ label: e.target.value })}
          disabled={disable}
        />
      ) : isRow || isColumn ? (
        <>
          <TextField
            label="Container Title"
            fullWidth
            sx={{ mb: 2 }}
            value={f.title || ""}
            onChange={(e) => set({ title: e.target.value })}
            disabled={disable}
          />
          {isRow && (
            <TextField
              label="Columns"
              fullWidth
              sx={{ mb: 2 }}
              value={2}
              disabled
              helperText="Row Layout currently supports 2 columns."
            />
          )}
          {isColumn && (
            <TextField
              label="Columns"
              fullWidth
              sx={{ mb: 2 }}
              value={1}
              disabled
              helperText="Column Layout currently supports 1 column."
            />
          )}
          <Typography variant="caption" color="text.secondary">
            Place fields into this container from Field Settings (Group section) or using the Add buttons in the container.
          </Typography>
        </>
      ) : (
        <>
          <TextField
            label="Label"
            fullWidth
            sx={{ mb: 2 }}
            value={f.label || ""}
            onChange={(e) => set({ label: e.target.value })}
            disabled={disable}
          />
          <TextField
            label="Name (data key)"
            fullWidth
            sx={{ mb: 2 }}
            value={f.name || ""}
            onChange={(e) => set({ name: e.target.value })}
            disabled={disable}
          />
          <TextField
            label="Placeholder"
            fullWidth
            sx={{ mb: 2 }}
            value={f.placeholder || ""}
            onChange={(e) => set({ placeholder: e.target.value })}
            disabled={disable}
          />
          <TextField
            label="Helper text"
            fullWidth
            sx={{ mb: 2 }}
            value={f.helperText || ""}
            onChange={(e) => set({ helperText: e.target.value })}
            disabled={disable}
          />

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
              Width on form
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={f?.layout?.colSpan === 2 ? 2 : 1}
              onChange={(_, val) => val && set({ layout: { ...(f.layout || {}), colSpan: val } })}
              disabled={disable}
            >
              <ToggleButton value={1}>Half</ToggleButton>
              <ToggleButton value={2}>Full</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={!!f.required}
                onChange={(e) => set({ required: e.target.checked })}
                disabled={disable}
              />
            }
            label="Required"
          />

          {/* Grouping into row containers */}
          {showGroupingControls && (
            <Box sx={{ mt: 2, p: 1.5, border: "1px solid #E5E7EB", borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Group (Row Layout)
              </Typography>

              <Select
                size="small"
                fullWidth
                value={f.groupId || ""}
                onChange={(e) => {
                  const nextGroup = e.target.value || null;
                  if (!nextGroup) {
                    set({ groupId: null, groupCol: null });
                    return;
                  }
                  // default to Column A unless already set
                  set({ groupId: nextGroup, groupCol: f.groupCol ?? 0, groupOrder: Date.now() });
                }}
                disabled={disable}
              >
                <MenuItem value="">No group</MenuItem>
                {rowContainers.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>

              {f.groupId && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                    Column
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={typeof f.groupCol === "number" ? f.groupCol : 0}
                    onChange={(_, val) => {
                      if (val == null) return;
                      set({ groupCol: val, groupOrder: Date.now() });
                    }}
                    disabled={disable}
                  >
                    <ToggleButton value={0}>A</ToggleButton>
                    <ToggleButton value={1}>B</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {(f.type === "select" || f.type === "multiselect" || f.type === "radio") && (
            <Box sx={{ my: 2, border: "1px solid #e0e0e0", borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Options
              </Typography>

              {(f.options || []).map((opt, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <TextField
                    label="Label"
                    size="small"
                    value={opt.label}
                    onChange={(e) => handleOptionChange(index, "label", e.target.value)}
                    fullWidth
                    disabled={disable}
                  />
                  <TextField
                    label="Value"
                    size="small"
                    value={opt.value}
                    onChange={(e) => handleOptionChange(index, "value", e.target.value)}
                    fullWidth
                    disabled={disable}
                  />
                  <IconButton onClick={() => handleRemoveOption(index)} size="small" disabled={disable}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
              ))}

              <ButtonComponent
                label="Add Option"
                type="secondary"
                onClick={handleAddOption}
                startIcon={<AddCircleOutlineIcon />}
                size="small"
                fullWidth
                disabled={disable}
              />
            </Box>
          )}

          {f.type === "radio" && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!f.inline}
                  onChange={(e) => set({ inline: e.target.checked })}
                  disabled={disable}
                />
              }
              label="Display options inline"
            />
          )}

          <Divider sx={{ my: 2 }} />

          <TextField
            label="Validation"
            select
            fullWidth
            value={f.validationPreset || "none"}
            onChange={(e) => handlePresetChange(e.target.value)}
            sx={{ mb: 1 }}
            disabled={disable}
          >
            {PRESETS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>

          {f.validationPreset === "custom" && (
            <Box sx={{ display: "grid", gap: 1, my: 1 }}>
              <TextField
                label="Custom pattern (RegExp)"
                fullWidth
                value={f.pattern || ""}
                onChange={(e) => set({ pattern: e.target.value })}
                disabled={disable}
              />
              <TextField
                label="Validation message"
                fullWidth
                value={f.patternMessage || ""}
                onChange={(e) => set({ patternMessage: e.target.value })}
                disabled={disable}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}